import React, { useEffect, useMemo, useState } from 'react';
import {
    buildCustomMilestoneSchedule,
    buildInitialCustomScheduleRows,
    customRowsToSchedulePayload,
    splitLockedEscrowFee,
    toDatetimeLocalValue,
    validateCustomMilestoneSchedule,
} from '../../utils/milestoneSchedule';
import { formatNumber } from '../../utils/commonFunction';
import { ethers } from 'ethers';

const DEFAULT_ADD_INTERVAL_SEC = 86400;

const CustomMilestoneScheduleEditor = ({
    grossAmount,
    feeBps = 0,
    tokenDecimals = 6,
    tokenSymbol = '',
    rows,
    onRowsChange,
    onValidationChange,
}) => {
    const [touched, setTouched] = useState(false);

    const netEscrowInfo = useMemo(() => {
        const grossHuman = Number(grossAmount);
        if (!grossHuman || grossHuman <= 0) return null;
        try {
            const grossWei = ethers.parseUnits(String(grossAmount), tokenDecimals);
            const { feeWei, netEscrowWei } = splitLockedEscrowFee(grossWei, feeBps);
            return {
                grossFormatted: formatNumber(grossHuman),
                feeFormatted: ethers.formatUnits(feeWei, tokenDecimals),
                netFormatted: ethers.formatUnits(netEscrowWei, tokenDecimals),
                netEscrowWei: netEscrowWei.toString(),
            };
        } catch (_e) {
            return null;
        }
    }, [grossAmount, feeBps, tokenDecimals]);

    useEffect(() => {
        if (touched || rows.length > 0) return;
        const initial = buildInitialCustomScheduleRows({
            grossAmount,
            feeBps,
            decimals: tokenDecimals,
        });
        if (initial.length) {
            onRowsChange(initial);
        }
    }, [grossAmount, feeBps, tokenDecimals, touched, rows.length, onRowsChange]);

    const validation = useMemo(() => {
        const payload = customRowsToSchedulePayload(rows, tokenDecimals);
        if (!payload || !netEscrowInfo?.netEscrowWei) {
            return { ok: false, error: 'Enter amount and milestones' };
        }
        return validateCustomMilestoneSchedule({
            unlockTimesSec: payload.unlockTimesSec,
            milestoneAmountsWei: payload.milestoneAmountsWei,
            netEscrowWei: netEscrowInfo.netEscrowWei,
        });
    }, [rows, tokenDecimals, netEscrowInfo]);

    useEffect(() => {
        onValidationChange?.(validation);
    }, [validation, onValidationChange]);

    const preview = useMemo(() => {
        const payload = customRowsToSchedulePayload(rows, tokenDecimals);
        if (!payload || !validation.ok) return null;
        return buildCustomMilestoneSchedule({
            unlockTimesSec: payload.unlockTimesSec,
            milestoneAmounts: payload.milestoneAmountsWei.map((wei) =>
                Number(ethers.formatUnits(wei, tokenDecimals)),
            ),
        });
    }, [rows, tokenDecimals, validation.ok]);

    const updateRow = (id, patch) => {
        setTouched(true);
        onRowsChange(
            rows.map((row) => (row.id === id ? { ...row, ...patch } : row)),
        );
    };

    const addRow = () => {
        setTouched(true);
        const last = rows[rows.length - 1];
        const lastUnlockMs = last?.unlockAt
            ? new Date(last.unlockAt).getTime()
            : Date.now();
        const nextUnlock = toDatetimeLocalValue(
            lastUnlockMs + DEFAULT_ADD_INTERVAL_SEC * 1000,
        );
        onRowsChange([
            ...rows,
            {
                id: Date.now(),
                amount: '',
                unlockAt: nextUnlock,
            },
        ]);
    };

    const removeRow = (id) => {
        if (rows.length <= 1) return;
        setTouched(true);
        onRowsChange(rows.filter((row) => row.id !== id));
    };

    const sym = tokenSymbol ? ` ${tokenSymbol}` : '';

    return (
        <div className="space-y-3">
            {netEscrowInfo && (
                <div className="rounded-xl border border-zinc-200 bg-zinc-50/80 px-3 py-2.5 text-xs text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-zinc-400">
                    <p>
                        Gross lock:{' '}
                        <span className="font-semibold text-zinc-900 dark:text-white">
                            {netEscrowInfo.grossFormatted}
                            {sym}
                        </span>
                        {Number(feeBps) > 0 && (
                            <>
                                {' '}
                                · Fee ({Number(feeBps) / 100}%):{' '}
                                <span className="font-medium">
                                    {formatNumber(Number(netEscrowInfo.feeFormatted))}
                                    {sym}
                                </span>
                            </>
                        )}
                    </p>
                    <p className="mt-1">
                        Milestone amounts must sum to net escrow:{' '}
                        <span className="font-semibold text-teal-800 dark:text-emerald-300">
                            {formatNumber(Number(netEscrowInfo.netFormatted))}
                            {sym}
                        </span>
                    </p>
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                        Custom unlock schedule
                    </p>
                    <p className="mt-0.5 text-[10px] text-zinc-500 dark:text-zinc-500">
                        Set each unlock time and amount manually.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={addRow}
                    className="rounded-lg border border-teal-200 px-2.5 py-1 text-[11px] font-semibold text-teal-800 hover:bg-teal-50 dark:border-emerald-500/30 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
                >
                    + Add milestone
                </button>
            </div>

            <div className="overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800">
                <table className="w-full text-left text-xs">
                    <thead className="border-b border-zinc-200 bg-zinc-50 text-[10px] font-bold uppercase tracking-wide text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/80">
                        <tr>
                            <th className="px-3 py-2">#</th>
                            <th className="px-3 py-2">Unlock at</th>
                            <th className="px-3 py-2">Amount</th>
                            <th className="px-3 py-2 w-10" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800/80">
                        {rows.map((row, index) => (
                            <tr key={row.id}>
                                <td className="px-3 py-2 font-mono text-zinc-500">
                                    {index + 1}
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="datetime-local"
                                        value={row.unlockAt}
                                        onChange={(e) =>
                                            updateRow(row.id, {
                                                unlockAt: e.target.value,
                                            })
                                        }
                                        className="cpg-input w-full min-w-[160px] px-2 py-1.5 text-xs"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <input
                                        type="number"
                                        min={0}
                                        step="any"
                                        value={row.amount}
                                        onChange={(e) =>
                                            updateRow(row.id, {
                                                amount: e.target.value,
                                            })
                                        }
                                        placeholder="0.00"
                                        className="cpg-input w-full min-w-[100px] px-2 py-1.5 text-xs tabular-nums"
                                    />
                                </td>
                                <td className="px-3 py-2">
                                    <button
                                        type="button"
                                        onClick={() => removeRow(row.id)}
                                        disabled={rows.length <= 1}
                                        className="text-zinc-400 hover:text-rose-500 disabled:opacity-30"
                                        aria-label="Remove milestone"
                                    >
                                        <i className="fa-solid fa-trash-can text-xs" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {!validation.ok && rows.length > 0 && (
                <p className="text-xs text-rose-600 dark:text-rose-400">
                    <i className="fa-solid fa-circle-exclamation mr-1" />
                    {validation.error}
                </p>
            )}

            {preview && (
                <p className="text-[11px] text-zinc-500 dark:text-zinc-500">
                    {preview.milestoneCount} milestones · final unlock{' '}
                    {preview.endDateLabel}
                </p>
            )}
        </div>
    );
};

export default CustomMilestoneScheduleEditor;
